import type { SupabaseClient } from '@supabase/supabase-js'
import { orderBy } from 'lodash-es'
import type { Database, Tables } from '../db/supabase.types.ts'
import { type AssetPriceType } from './assetTypes.ts'

export type AssetPricePayload = {
  assetId: string
  price: number
  priceType?: AssetPriceType
  createdAt?: string
}

export async function addAssetPrice(
  { assetId, price, priceType = 'asking_price', createdAt }: AssetPricePayload,
  ctx: { supabase: SupabaseClient<Database> }
) {
  const { supabase } = ctx

  const { data: addedPrice, error } = await supabase
    .from('asset_prices')
    .insert({
      currency: 'EUR', // TODO: make this configurable when we have multiple currencies
      asset_id: assetId,
      price,
      price_type: priceType,
      created_at: createdAt ? new Date(createdAt).toISOString() : new Date().toISOString(),
    })
    .select()
    .single()

  if (error || !addedPrice) {
    console.error(error)
    return null
  }

  return addedPrice
}

export async function addLatestPriceOfType(
  {
    assetId,
    priceType,
    currentPrices,
    proposedPrice,
  }: {
    assetId: string
    currentPrices: Tables<'asset_prices'>[]
    proposedPrice: number
    priceType: AssetPriceType
  },
  ctx: { supabase: SupabaseClient<Database> }
) {
  const priceToAdd = getPriceToAdd(currentPrices, proposedPrice, priceType)

  if (priceToAdd) {
    const priceData = {
      assetId,
      price: priceToAdd,
      priceType,
    }

    return addAssetPrice(priceData, ctx)
  }

  return null
}

export function getPriceToAdd(
  currentPrices: Tables<'asset_prices'>[],
  proposedPrice: number | null,
  priceType: AssetPriceType
) {
  if (!proposedPrice) {
    return null
  }

  const latestAskingPrice = getLatestPrice(currentPrices, priceType)

  if (!latestAskingPrice?.price || latestAskingPrice.price !== proposedPrice) {
    return proposedPrice
  }

  return null
}

export function getLatestPrice(
  prices: Tables<'asset_prices'>[],
  priceType: AssetPriceType
): Tables<'asset_prices'> | undefined {
  return orderBy(
    prices?.filter((p) => p.price_type === priceType && !p.deleted_at),
    'created_at',
    'desc'
  )[0]
}

export function getDisplayPrice(prices: Tables<'asset_prices'>[]) {
  const askingPrice = getLatestPrice(prices, 'asking_price')

  if (askingPrice) {
    return askingPrice
  }

  return getLatestPrice(prices, 'estimated_price')
}

export async function updateFinalPrice(
  assetId: string,
  finalPrice: number,
  currentPrices: Tables<'asset_prices'>[],
  ctx: { supabase: SupabaseClient<Database> }
) {
  const { supabase } = ctx
  const existingFinalPrice = getLatestPrice(currentPrices, 'final_price')

  if (existingFinalPrice) {
    if (existingFinalPrice.price === finalPrice) {
      return null
    }

    const { error } = await supabase
      .from('asset_prices')
      .update({ price: finalPrice })
      .eq('id', existingFinalPrice.id)

    if (error) {
      console.error('Error updating final price:', error)
      return null
    }

    return { id: existingFinalPrice.id }
  }

  if (finalPrice > 0) {
    const { data: newPrice, error } = await supabase
      .from('asset_prices')
      .insert({
        currency: 'EUR',
        asset_id: assetId,
        price: finalPrice,
        price_type: 'final_price',
      })
      .select()
      .single()

    if (error) {
      console.error('Error inserting final price:', error)
      return null
    }

    return newPrice
  }

  return null
}
